import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Factory, ChevronLeft, ShieldCheck, Truck, Minus, Plus, Hammer } from "lucide-react";
import { StoreLayout } from "@/components/StoreLayout";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { CustomizeYourPiece } from "@/components/CustomizeYourPiece";
import { ProductCard } from "@/components/ProductCard";
import { ImageZoomViewer } from "@/components/ImageZoomViewer";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { api, formatNum, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { wishlistIds, refreshWishlist, shopLoggedIn } = useAuth();
  const { addToCart, getCartQty } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const heroImgRef = useRef(null);
  const [carouselApi, setCarouselApi] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [customPricing, setCustomPricing] = useState(null);
  const [customMeta, setCustomMeta] = useState({ isDefaultCombo: true, inStock: false, productionDays: 15 });
  const [requestingBuild, setRequestingBuild] = useState(false);
  const isWished = product && wishlistIds.includes(product.id);
  // Diamond carat total is recomputed on every render otherwise (reduce + toFixed) —
  // memoize it so it only recalculates when the loaded product actually changes.
  const diamondCaratTotal = useMemo(
    () => (product?.diamonds || []).reduce((a, d) => a + (Number(d.carat) || 0), 0).toFixed(2),
    [product]
  );
  // A product may mix natural + lab-grown diamond lines — show every distinct type present
  // (e.g. "Natural" or "Natural + Lab-Grown") rather than assuming a single type.
  const diamondTypeLabel = useMemo(() => {
    const types = new Set((product?.diamonds || []).map((d) => (d.diamond_type === "lab" ? "Lab-Grown" : "Natural")));
    return Array.from(types).join(" + ");
  }, [product]);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setActiveImg(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    onSelect();
    return () => { carouselApi.off("select", onSelect); };
  }, [carouselApi]);

  // "You may also like": prefer same-category matches; if fewer than 4 are found,
  // top up the shelf with other (shuffled, recent) products so the section never looks sparse.
  const loadRelated = async (data) => {
    try {
      const { data: sameCat } = await api.get(`/products?category=${encodeURIComponent(data.category)}`);
      let pool = sameCat.filter((r) => r.id !== data.id);
      if (pool.length < 4) {
        const { data: recent } = await api.get("/products?sort=newest");
        const extra = recent.filter((r) => r.id !== data.id && !pool.some((p) => p.id === r.id));
        for (let i = extra.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [extra[i], extra[j]] = [extra[j], extra[i]];
        }
        pool = [...pool, ...extra];
      }
      setRelated(pool.slice(0, 4));
    } catch {
      // Related products are a non-critical enhancement — fail silently.
    }
  };

  // If the hero image is already in the browser cache (e.g. it was just shown as a
  // related-product thumbnail, or the shopper is revisiting a product), the <img>'s
  // `load` event can fire — or the image can already be `complete` — before React
  // attaches the `onLoad` handler below. Without this check, `imgLoaded` would get
  // stuck false and the image would stay invisible (opacity-0) forever on a cached hit.
  useLayoutEffect(() => {
    if (heroImgRef.current?.complete) setImgLoaded(true);
  }, [product]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setImgLoaded(false);
    setQty(1);
    setCustomPricing(null);
    setCustomMeta({ isDefaultCombo: true, inStock: false, productionDays: 15 });
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data); setActiveImg(0);
      setCustomPricing(data.pricing);
      setCustomMeta({ isDefaultCombo: true, inStock: Number(data.stock ?? 0) > 0, productionDays: 15 });
      loadRelated(data);
    }).catch(() => toast.error("Product not found"));
  }, [id]);

  const toggleWishlist = async () => {
    if (!shopLoggedIn) { toast.error("Please log in"); navigate("/login"); return; }
    try {
      if (isWished) { await api.delete(`/wishlist/${product.id}`); toast("Removed from wishlist"); }
      else { await api.post(`/wishlist/${product.id}`); toast.success("Added to wishlist"); }
      refreshWishlist();
    } catch { toast.error("Action failed"); }
  };

  if (!product) return <StoreLayout><div className="max-w-6xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading...</div></StoreLayout>;

  const images = product.images && product.images.length ? product.images : ["https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=800&q=80"];
  const stock = Number(product.stock ?? 0);
  const inStock = customMeta.isDefaultCombo ? stock > 0 : false;
  const isCustomBuild = !customMeta.isDefaultCombo;
  const cartQty = getCartQty ? getCartQty(product.id) : 0;
  const atLimit = inStock && (cartQty + qty) >= stock;
  const decQty = () => setQty((q) => Math.max(1, q - 1));
  const incQty = () => setQty((q) => (inStock ? Math.min(Math.max(stock - cartQty, 1), q + 1) : q + 1));

  const handleAddToCart = async () => {
    if (!shopLoggedIn) { toast.error("Please log in"); navigate("/login"); return; }
    setAdding(true);
    const ok = await addToCart(product, qty);
    setAdding(false);
    if (ok) setQty(1);
  };

  const handleRequestCustomBuild = async () => {
    if (!shopLoggedIn) { toast.error("Please log in"); navigate("/login"); return; }
    setRequestingBuild(true);
    try {
      await api.post("/custom-build-requests", {
        product_id: product.id, quantity: qty,
        customization: {
          karat: customPricing?.gold?.karat, gold_color: customPricing?.gold?.color,
          diamond_type: (customPricing?.diamonds || [])[0]?.diamond_type,
        },
      });
      toast.success(`Custom build request submitted — Made to Order (${customMeta.productionDays} days for production)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit the custom build request");
    } finally { setRequestingBuild(false); }
  };

  return (
    <StoreLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 gap-1 -ml-2"><ChevronLeft className="h-4 w-4" /> Back</Button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Gallery */}
          <div data-testid="pdp-image-gallery" className="min-w-0">
            <Carousel className="relative" opts={{ loop: images.length > 1 }} setApi={setCarouselApi}>
              <CarouselContent>
                {images.map((img, i) => (
                  <CarouselItem key={`${img}-${i}`}>
                    <button
                      type="button"
                      data-testid="pdp-zoom-trigger"
                      onClick={() => { setZoomIndex(i); setZoomOpen(true); }}
                      className="block w-full text-left cursor-zoom-in rounded-2xl overflow-hidden border border-border bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
                    >
                      <AspectRatio ratio={1}><img ref={i === 0 ? heroImgRef : undefined} src={resolveImg(img)} alt={`${product.name} ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} decoding="async" onLoad={() => i === 0 && setImgLoaded(true)} className={`h-full w-full object-cover transition-opacity duration-300 ${i === 0 && !imgLoaded ? "opacity-0" : "opacity-100"}`} /></AspectRatio>
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {images.length > 1 && (
                <>
                  <CarouselPrevious data-testid="pdp-carousel-prev" className="left-3" />
                  <CarouselNext data-testid="pdp-carousel-next" className="right-3" />
                </>
              )}
            </Carousel>
            {images.length > 1 && (
              <div className="flex gap-3 mt-3 flex-wrap">
                {images.map((img, i) => (
                  <button key={`${img}-${i}`} data-testid="pdp-thumb" onClick={() => carouselApi && carouselApi.scrollTo(i)} className={`h-20 w-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-[hsl(var(--primary))]" : "border-border"}`}>
                    <img src={resolveImg(img)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight">{product.name}</h1>
            <p className="text-xs text-muted-foreground mt-1 font-mono">SKU: {product.sku}</p>
            <p className="text-muted-foreground mt-3">{product.description}</p>

            {/* Specs — 5 equal-sized boxes: Gross Weight / Net Weight / Gold Purity / Diamond Weight / Diamond Grade */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5 text-sm">
              {product.gold?.weight_g > 0 && (
                <>
                  <div className="rounded-lg border border-border bg-card p-3"><div className="text-muted-foreground text-xs">Gross Weight</div><div className="font-mono">{formatNum(product.gold.gross_weight_g || product.gold.weight_g)}g</div></div>
                  <div className="rounded-lg border border-border bg-card p-3"><div className="text-muted-foreground text-xs">Net Weight</div><div className="font-mono">{formatNum(product.gold.weight_g)}g</div></div>
                  <div className="rounded-lg border border-border bg-card p-3"><div className="text-muted-foreground text-xs">Gold Purity</div><div className="font-mono">{product.gold.karat}</div></div>
                </>
              )}
              {(product.diamonds || []).length > 0 && (
                <>
                  <div data-testid="pdp-diamond-spec" className="rounded-lg border border-border bg-card p-3"><div className="text-muted-foreground text-xs">Diamond Weight</div><div className="font-mono">{diamondCaratTotal}ct</div></div>
                  <div className="rounded-lg border border-border bg-card p-3"><div className="text-muted-foreground text-xs">Diamond Grade</div><div className="font-mono">{product.diamonds[0]?.grade_label || product.diamonds[0]?.quality || "—"}</div></div>
                  <div data-testid="pdp-diamond-type-spec" className="rounded-lg border border-border bg-card p-3"><div className="text-muted-foreground text-xs">Diamond Type</div><div className="font-mono">{diamondTypeLabel}</div></div>
                </>
              )}
            </div>

            <div className="mt-6">
              <CustomizeYourPiece product={product} onChange={(pricing, meta) => { setCustomPricing(pricing); setCustomMeta(meta); }} />
              <PriceBreakdown pricing={customPricing || product.pricing} />
            </div>

            {/* Primary action + quantity + wishlist — one row, all three the same h-12 height */}
            <div className="flex items-center gap-3 mt-6">
              {isCustomBuild ? (
                <Button
                  data-testid="pdp-request-custom-build-button"
                  onClick={handleRequestCustomBuild}
                  disabled={requestingBuild}
                  className="flex-1 min-w-0 gap-2 h-12"
                >
                  <Hammer className="h-4 w-4 shrink-0" />
                  <span className="text-base font-bold truncate">Request Custom Build</span>
                </Button>
              ) : (
                <Button
                  data-testid={inStock ? "pdp-add-to-cart-button" : "pdp-make-to-order-button"}
                  onClick={handleAddToCart}
                  disabled={adding}
                  className="flex-1 min-w-0 gap-2 h-12"
                >
                  {inStock ? <ShoppingBag className="h-4 w-4 shrink-0" /> : <Factory className="h-4 w-4 shrink-0" />}
                  <span className="text-base font-bold truncate">{inStock ? "Add to Cart" : "Make to Order"}</span>
                </Button>
              )}

              <div className="flex items-center h-12 border border-border rounded-lg shrink-0">
                <button data-testid="pdp-qty-decrease" onClick={decQty} disabled={qty <= 1} className="px-3 h-full disabled:opacity-40 disabled:pointer-events-none hover:bg-[hsl(var(--gold-light))] transition-colors duration-150">
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span data-testid="pdp-qty-value" className="px-4 font-mono text-sm">{qty}</span>
                <button data-testid="pdp-qty-increase" onClick={incQty} disabled={atLimit} title={atLimit ? "No more available" : ""} className="px-3 h-full disabled:opacity-40 disabled:pointer-events-none hover:bg-[hsl(var(--gold-light))] transition-colors duration-150">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              <Button data-testid="pdp-wishlist-toggle" variant="outline" onClick={toggleWishlist} className="h-12 w-12 p-0 flex-shrink-0">
                <Heart className={`h-5 w-5 ${isWished ? "fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" : ""}`} />
              </Button>
            </div>
            {inStock && atLimit && <span className="text-xs text-muted-foreground block mt-1.5">Max available reached</span>}
            <div className="flex items-center gap-6 mt-5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Certified & hallmarked</span>
              <span className="flex items-center gap-1.5"><Truck className="h-4 w-4" /> Quick And Insured Delivery</span>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="font-display text-2xl mb-6">You may also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{related.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          </section>
        )}
      </div>
      <ImageZoomViewer
        images={images}
        index={zoomIndex}
        onIndexChange={setZoomIndex}
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        alt={product.name}
      />
    </StoreLayout>
  );
}

